import { Button, Skeleton } from "@/design-system/components/ui";
import { relativeTime } from "@/components/ux/relativeTime";
import { useActivityByOwner } from "@/pages/activity/hooks/useActivityByOwner";
import { DetailSection } from "../detailBits";
import { InlineQueryError } from "../queryStates";

/**
 * Layout Standard §4.1's one sanctioned exception — up to five system events,
 * read-only, on the Overview rail.
 *
 * <p>🔴 <b>This is the ONLY feed allowed on Overview, and it is why the
 * conversation log moved to a tab of its own.</b> §4.1 forbids call logs,
 * communication entries and message previews there in as many words; the reason
 * a specialist wanted the recent conversation on Overview is served by this card
 * instead, because an event feed says <i>something happened</i> without
 * reproducing the content of a candid internal note on the page a reader lands
 * on. Do not add a second preview beside it.</p>
 *
 * <p><b>Why not the shared <c>OverviewActivityPreview</c>.</b> That component
 * renders "See all activity →" as a react-router <c>&lt;Link&gt;</c> to
 * <c>{detailPath}?tab=activity</c>. This body is also rendered inside a record
 * drawer over some other page, where following that link would navigate the HOST
 * page out from under the open drawer — the same defect the buyers-presented
 * table is being converted away from. Here "See all" is a button that switches
 * the tab the caller owns, which does the right thing at a record's own address
 * and in a drawer alike. It reads the same
 * <c>useActivityByOwner</c> hook against the same endpoint, so there is no
 * second query path and no second cache key.</p>
 *
 * <p>A failed read says so and offers a retry. The shared QueryClient runs at
 * <c>staleTime: 5min</c> with <c>refetchOnWindowFocus: false</c>, so silence
 * here would persist for minutes with no route back — and "No activity yet" on a
 * record that has plenty is a claim about the record made on the strength of a
 * failed request.</p>
 */
export function SubmissionActivityPreview({
  submissionId,
  onSeeAll,
}: {
  submissionId: string;
  onSeeAll: () => void;
}) {
  const list = useActivityByOwner("buyer_submission", submissionId, {
    take: PREVIEW_COUNT,
  });
  const events = list.data?.data ?? [];

  return (
    <DetailSection
      title="Recent activity"
      action={
        <Button
          variant="text"
          size="sm"
          data-testid="submission-activity-see-all"
          onClick={onSeeAll}
        >
          See all →
        </Button>
      }
    >
      {list.isLoading && <Skeleton className="h-16 w-full" />}

      {!list.isLoading && list.isError && (
        <InlineQueryError
          what="recent activity"
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      )}

      {!list.isLoading && !list.isError && events.length === 0 && (
        <p className="text-small text-gray-mid m-0">
          Nothing recorded on this submission yet.
        </p>
      )}

      {events.length > 0 && (
        <ul
          className="m-0 p-0 list-none divide-y divide-border-light"
          data-testid="submission-activity-preview"
        >
          {events.map((event) => (
            <li key={event.id} className="py-space-2 first:pt-0 last:pb-0 min-w-0">
              <div className="flex items-baseline justify-between gap-space-2 min-w-0">
                <span className="text-small text-onyx min-w-0 break-words">
                  {event.headline}
                </span>
                <span className="text-label text-gray-mid shrink-0">
                  {relativeTime(event.occurredAt)}
                </span>
              </div>
              <div className="text-label text-gray-mid mt-space-1 break-words">
                {event.actorDisplayName}
                {event.subline ? ` · ${event.subline}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

/** Layout Standard §4.1 — the cap is five, and it is not a preference. */
const PREVIEW_COUNT = 5;
